import { useLayoutEffect, useRef, useState } from "react";
import { sectionContent, type SectionType } from "@baze/schema";
import { t } from "@/i18n/id";

/**
 * Click-to-edit text on the editor canvas. The engine marks editable text with `data-field="content.…"`;
 * free-text string fields get an overlay textarea that copies the element's typography, so typing looks
 * like editing the page itself. Every keystroke is applied to the draft (coalesced in history), so the
 * page reflows live and the inspector stays in sync.
 */

export interface TextSpec {
  max: number;
  /** Clearing a required field restores the previous text. Optional fields are removed when cleared. */
  required: boolean;
  /** Long fields (descriptions, body, answers) accept line breaks; short ones commit on Enter. */
  multiline: boolean;
}

export interface InlineTarget {
  sectionId: string;
  path: string;
  initial: string;
  spec: TextSpec;
}

type ZodLike = {
  _zod: { def: { type: string } };
  unwrap?: () => ZodLike;
  element?: ZodLike;
  shape?: Record<string, ZodLike>;
  maxLength?: number | null;
  minLength?: number | null;
  safeParse: (v: unknown) => { success: boolean };
};

/** Describe the content field at `path` when it is plain, free text (not a time, URL, price, image…). */
export function textSpec(type: SectionType, path: string): TextSpec | null {
  const segs = path.split(".");
  if (segs[0] !== "content" || segs.length < 2) return null;
  let cur = sectionContent[type] as unknown as ZodLike | undefined;
  let required = true;
  const unwrap = () => {
    while (cur && cur._zod.def.type === "optional" && cur.unwrap) {
      required = false;
      cur = cur.unwrap();
    }
  };
  for (const seg of segs.slice(1)) {
    unwrap();
    if (!cur) return null;
    if (/^\d+$/.test(seg)) {
      if (cur._zod.def.type !== "array" || !cur.element) return null;
      cur = cur.element;
      // An array entry is required for its own sake, whatever the array is.
      required = true;
    } else {
      if (cur._zod.def.type !== "object" || !cur.shape) return null;
      cur = cur.shape[seg];
    }
  }
  unwrap();
  if (!cur || cur._zod.def.type !== "string") return null;
  // Patterned strings (times, dates, account numbers) are edited in the inspector, where they are explained.
  if (!cur.safeParse("Teks contoh").success) return null;
  const max = cur.maxLength ?? 300;
  return { max, required: required && (cur.minLength ?? 0) > 0, multiline: max >= 240 };
}

/** The canvas element for a field, looked up fresh (sections re-render on every keystroke). Some fields
 *  exist twice (a navbar button in the bar and in the mobile menu): prefer the one that is on screen. */
function findField(root: HTMLElement, sectionId: string, path: string): HTMLElement | null {
  const all = root.querySelectorAll<HTMLElement>(`[data-baze-section="${CSS.escape(sectionId)}"] [data-field="${CSS.escape(path)}"]`);
  return [...all].find((el) => el.getClientRects().length > 0) ?? all[0] ?? null;
}

/** Properties that decide how text is laid out; the overlay copies them so typing looks like the page itself. */
const TEXT_PROPS = [
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "font-stretch",
  "font-variant",
  "font-feature-settings",
  "font-variation-settings",
  "font-kerning",
  "font-optical-sizing",
  "letter-spacing",
  "word-spacing",
  "line-height",
  "text-transform",
  "text-align",
  "text-indent",
  "text-rendering",
  "text-wrap-mode",
  "text-wrap-style",
  "word-break",
  "overflow-wrap",
  "hyphens",
  "direction",
  "tab-size",
  "-webkit-font-smoothing",
] as const;

const px = (v: string) => Number.parseFloat(v) || 0;

/** The field's first non-blank text node. */
function firstText(field: HTMLElement): Text | null {
  const walker = document.createTreeWalker(field, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeValue?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  return walker.nextNode() as Text | null;
}

/** Line fragments of every text node in the field (a wrapper's own range would also list child boxes). */
function textRects(field: HTMLElement): DOMRect[] {
  const walker = document.createTreeWalker(field, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeValue?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  const out: DOMRect[] = [];
  const range = document.createRange();
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    range.selectNodeContents(n);
    for (const r of range.getClientRects()) if (r.width > 0) out.push(r);
  }
  return out;
}

/** The element that directly holds the field's first text: where the typography really lives. */
function textHolder(field: HTMLElement): HTMLElement {
  return firstText(field)?.parentElement ?? field;
}

/** The block that lays the text lines out: the text holder, or its nearest non-inline ancestor inside the field. */
function lineBox(holder: HTMLElement, field: HTMLElement): HTMLElement {
  let el = holder;
  while (el !== field && getComputedStyle(el).display.startsWith("inline") && el.parentElement) el = el.parentElement;
  return el;
}

/**
 * Click-to-edit overlay. A transparent textarea is laid exactly over the text's content box and first
 * line (not over the element's outer box), so the text does not move when editing starts. The original
 * text is only made transparent, never hidden: backgrounds, borders and layout of buttons and badges
 * stay put and resize live as the draft changes.
 */
export function InlineTextEditor({
  root,
  target,
  value,
  onChange,
  onDone,
  onTab,
}: {
  root: HTMLElement;
  target: InlineTarget;
  value: string;
  onChange: (value: string | undefined) => void;
  onDone: () => void;
  /** Tab / Shift+Tab: move to the next / previous editable text (the parent falls back to closing). */
  onTab: (dir: 1 | -1) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState(value);
  const { spec } = target;

  useLayoutEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    let frame = 0;
    let marked: HTMLElement | null = null;
    let color = "";
    let shown = false;
    const place = () => {
      const el = findField(root, target.sectionId, target.path);
      if (marked && marked !== el) marked.removeAttribute("data-inline-editing");
      if (el) {
        // Read the style before the original text goes transparent.
        const holder = textHolder(el);
        const type = getComputedStyle(holder);
        if (!color) color = type.color;
        el.setAttribute("data-inline-editing", "");
        marked = el;
        const lines = lineBox(holder, el);
        const cs = getComputedStyle(lines);
        const box = lines.getBoundingClientRect();
        const base = root.getBoundingClientRect();
        const fontSize = px(type.fontSize);
        const lineHeight = type.lineHeight === "normal" ? fontSize * 1.25 : px(type.lineHeight);
        const rects = textRects(el);
        const first = rects[0];
        const multi = new Set(rects.map((r) => Math.round(r.top / 4))).size > 1;
        const contentLeft = box.left + px(cs.borderLeftWidth) + px(cs.paddingLeft);
        const contentWidth = box.width - px(cs.borderLeftWidth) - px(cs.borderRightWidth) - px(cs.paddingLeft) - px(cs.paddingRight);
        // Anchor on the first line's centre: inline text sits in a line box taller than its own rect.
        const centre = first ? first.top + first.height / 2 : box.top + px(cs.borderTopWidth) + px(cs.paddingTop) + lineHeight / 2;
        let left: number;
        let width: number;
        let indent = 0;
        if (multi || !first) {
          // Wrapped text: use the container, indenting the first line where a leading icon or glyph pushes it.
          left = contentLeft;
          width = contentWidth;
          if (first && /^(start|left)$/.test(type.textAlign)) indent = Math.max(0, first.left - contentLeft);
        } else {
          // One line: sit exactly where the glyphs are (after a chip's icon, centred in a block, ...).
          left = Math.min(...rects.map((r) => r.left));
          width = Math.max(...rects.map((r) => r.right)) - left;
        }
        for (const prop of TEXT_PROPS) ta.style.setProperty(prop, type.getPropertyValue(prop));
        ta.style.textIndent = `${indent}px`;
        if (!multi && first) ta.style.textAlign = "left";
        ta.style.color = color;
        ta.style.top = `${centre - base.top - lineHeight / 2}px`;
        ta.style.left = `${left - base.left}px`;
        ta.style.width = `${Math.max(width, 4)}px`;
        ta.style.whiteSpace = multi ? "pre-wrap" : "pre";
        ta.style.overflow = multi ? "hidden" : "visible";
        // Match the real text block's height (a textarea's scrollHeight also counts glyph overflow).
        const contentHeight = box.height - px(cs.borderTopWidth) - px(cs.borderBottomWidth) - px(cs.paddingTop) - px(cs.paddingBottom);
        ta.style.height = `${multi ? Math.max(contentHeight, lineHeight) : lineHeight}px`;
        if (!shown) {
          shown = true;
          ta.style.visibility = "visible";
          ta.focus({ preventScroll: true });
          ta.setSelectionRange(ta.value.length, ta.value.length);
        }
      }
      frame = requestAnimationFrame(place);
    };
    place();
    return () => {
      cancelAnimationFrame(frame);
      marked?.removeAttribute("data-inline-editing");
    };
  }, [root, target.sectionId, target.path]);

  const emit = (text: string) => {
    setDraft(text);
    if (!text.trim()) onChange(spec.required ? target.initial : undefined);
    else onChange(text);
  };

  const finish = () => {
    if (!draft.trim() && spec.required) onChange(target.initial);
    onDone();
  };

  return (
    <div className="baze-inline-editor pointer-events-none absolute inset-0 z-50">
      <textarea
        ref={ref}
        value={draft}
        maxLength={spec.max}
        rows={1}
        spellCheck
        data-field-path={target.path}
        aria-label={t.editor.inline.label}
        className="pointer-events-auto absolute m-0 resize-none rounded-[3px] border-0 bg-transparent p-0 outline-2 outline-offset-[3px] outline-[#4f46e5]"
        style={{ visibility: "hidden", boxSizing: "content-box" }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => emit(spec.multiline ? e.target.value : e.target.value.replace(/\s*\n\s*/g, " "))}
        onBlur={finish}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onChange(target.initial);
            onDone();
          } else if (e.key === "Enter" && (!spec.multiline || e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            finish();
          } else if (e.key === "Tab") {
            e.preventDefault();
            if (!draft.trim() && spec.required) onChange(target.initial);
            onTab(e.shiftKey ? -1 : 1);
          }
        }}
      />
    </div>
  );
}
